import {
  DownOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Input,
  InputRef,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import CustomerResponse, { DebtPaymentDto } from '../../model/Customer.model';
import CustomerService from '../../service/customer-service/Customer.service';
import ExcelService from '../../service/excel-service/Excel.service';
import { formatDate, formatMoney } from '../../utils/formatters';

const { Title, Text } = Typography;

interface ExpandedPaymentProps {
  debtPayments: DebtPaymentDto[] | undefined;
}

// Component for expanded row content
const ExpandedPayments: React.FC<ExpandedPaymentProps> = ({ debtPayments }) => {
  // Sort debt payments by date (oldest first)
  const sortedPayments = debtPayments
    ? [...debtPayments].sort(
        (a, b) =>
          new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
      )
    : [];

  const columns: ColumnsType<DebtPaymentDto> = [
    {
      title: 'Mã Thanh Toán',
      dataIndex: 'paymentId',
      key: 'paymentId',
    },
    {
      title: 'Ngày',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => formatDate(date),
    },
    {
      title: 'Số Tháng Quá Hạn',
      dataIndex: 'monthsSincePaymentDue',
      key: 'monthsSincePaymentDue',
      render: (months) => (
        <Tag color={months > 3 ? 'error' : months > 1 ? 'warning' : 'default'}>
          {months} tháng
        </Tag>
      ),
    },
    {
      title: 'Trạng Thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color;
        let text;

        switch (status) {
          case 'COMPLETED':
            color = 'success';
            text = 'Đã hoàn thành';
            break;
          case 'PENDING':
            color = 'warning';
            text = 'Đang chờ xử lý';
            break;
          case 'OVERDUE':
            color = 'error';
            text = 'Quá hạn';
            break;
          case 'PAID':
            color = 'processing';
            text = 'Đã thanh toán';
            break;
          default:
            color = 'default';
            text = status;
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Số Tiền',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount) => formatMoney(amount),
    },
  ];

  return (
    <div className="bg-gray-50 p-4 rounded-md">
      <Title level={5} className="mb-3">
        Chi Tiết Khoản Nợ
      </Title>
      <Table
        columns={columns}
        dataSource={sortedPayments}
        rowKey="paymentId"
        pagination={false}
        size="small"
        className="border border-gray-200 rounded"
      />
    </div>
  );
};

const DeptInLongTerm: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // State cho search
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await CustomerService.getCustomersWithLongTermDebt();
      setCustomers(data);
    } catch (err) {
      console.error('Error fetching long-term debt data:', err);
      setError('Không thể tải dữ liệu nợ dài hạn. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate total unpaid amount
  const totalUnpaidAmount = customers.reduce(
    (total, customer) => total + (customer.unpaidAmount || 0),
    0
  );

  // Get average months of debt
  const averageMonthsOfDebt = customers.length
    ? customers.reduce(
        (sum, customer) => sum + (customer.unpaidMonths || 0),
        0
      ) / customers.length
    : 0;

  // Hàm xử lý tìm kiếm
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (
    dataIndex: string
  ): ColumnsType<CustomerResponse>[0] => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Tìm kiếm`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Lọc
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const fieldValue = record[dataIndex as keyof CustomerResponse];
      return fieldValue
        ? fieldValue
            .toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // Hàm xử lý xuất Excel
  // const handleExportExcel = async () => {
  //   try {
  //     setExporting(true);
  //     await ExcelService.exportLongTermDebtToExcel();
  //     setExporting(false);
  //   } catch (err) {
  //     console.error('Error exporting to Excel:', err);
  //     setExporting(false);
  //   }
  // };

  const columns: ColumnsType<CustomerResponse> = [
    {
      title: 'Tên Khách Hàng',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Liên Hệ',
      dataIndex: 'contactInfo',
      key: 'contactInfo',
      ...getColumnSearchProps('contactInfo'),
    },
    {
      title: 'Thời Gian Nợ',
      dataIndex: 'unpaidMonths',
      key: 'unpaidMonths',
      align: 'center',
      render: (months) => (
        <Tag color={months > 3 ? 'error' : 'warning'} className="px-2 py-1">
          {months} tháng
        </Tag>
      ),
    },
    {
      title: 'Số Tiền Chưa Thanh Toán',
      dataIndex: 'unpaidAmount',
      key: 'unpaidAmount',
      align: 'right',
      render: (amount) => formatMoney(amount),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Title level={3}>Nợ Dài Hạn</Title>
        <div className="flex gap-2">
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            // onClick={handleExportExcel}
            loading={exporting}
            className="bg-green-600"
          >
            Xuất Excel
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
            className="bg-blue-500"
          >
            Làm Mới
          </Button>
        </div>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon className="mb-4" />
      )}

      <Card className="mb-4 bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Text className="text-gray-700 font-medium">
              Tổng Số Khách Hàng Có Nợ Dài Hạn:
            </Text>{' '}
            <Text>{customers.length}</Text>
          </div>
          <div>
            <Text className="text-gray-700 font-medium">
              Tổng Số Tiền Chưa Thanh Toán:
            </Text>{' '}
            <Text>{formatMoney(totalUnpaidAmount)}</Text>
          </div>
          <div>
            <Text className="text-gray-700 font-medium">
              Trung Bình Số Tháng Nợ:
            </Text>{' '}
            <Text>{averageMonthsOfDebt.toFixed(1)} tháng</Text>
          </div>
        </div>
      </Card>

      <Spin spinning={loading} tip="Đang tải dữ liệu...">
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedPayments debtPayments={record.debtPayments} />
            ),
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined
                  onClick={(e) => onExpand(record, e)}
                  className="text-blue-500 cursor-pointer"
                />
              ) : (
                <RightOutlined
                  onClick={(e) => onExpand(record, e)}
                  className="text-blue-500 cursor-pointer"
                />
              ),
          }}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Không tìm thấy khách hàng có nợ dài hạn' }}
          className="border border-gray-200 rounded"
        />
      </Spin>
    </div>
  );
};

export default DeptInLongTerm;
